import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  DollarSign, 
  Activity, 
  BookOpen, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  Target,
  Users,
  BarChart3,
  FileText,
  Settings,
  Calendar,
  Receipt,
  CircleDollarSign
} from 'lucide-react';
import { BACKEND_URL } from '../config';

const API = BACKEND_URL;

const ProjectManagerDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle = '' }) => (
    <Card>
      <CardContent className="flex items-center p-6">
        <div className={`p-2 rounded-lg bg-${color}-100 mr-4`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Project Manager Dashboard</h1>
            <p className="text-gray-600">Manage and oversee your assigned projects</p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Managed Projects" 
            value={dashboardData?.total_managed_projects || 0}
            icon={BookOpen}
            color="blue"
            subtitle="Total assigned projects"
          />
          <StatCard 
            title="Active Projects" 
            value={dashboardData?.active_projects || 0}
            icon={Activity}
            color="green"
            subtitle="Currently executing"
          />
          <StatCard 
            title="Total Budget" 
            value={formatCurrency(dashboardData?.total_budget || 0)}
            icon={DollarSign}
            color="yellow"
            subtitle="Across all projects"
          />
          <StatCard 
            title="Total Spent" 
            value={formatCurrency(dashboardData?.total_spent || 0)}
            icon={TrendingUp}
            color="purple"
            subtitle={`${Math.round(((dashboardData?.total_spent || 0) / (dashboardData?.total_budget || 1)) * 100)}% of budget`}
          />
        </div>

        {/* Action Items */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard 
            title="Pending Deliverables" 
            value={dashboardData?.pending_deliverables || 0}
            icon={FileText}
            color={dashboardData?.pending_deliverables > 0 ? 'red' : 'green'}
            subtitle="Requiring attention"
          />
          <StatCard 
            title="Open Issues" 
            value={dashboardData?.open_issues || 0}
            icon={AlertTriangle}
            color={dashboardData?.open_issues > 0 ? 'red' : 'green'}
            subtitle="Need resolution"
          />
          <StatCard 
            title="Pending Risks" 
            value={dashboardData?.pending_risks || 0}
            icon={Target}
            color={dashboardData?.pending_risks > 0 ? 'red' : 'green'}
            subtitle="Risk mitigation needed"
          />
        </div>

        {/* Project Management Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Project Management Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/projects">
                <Button className="w-full justify-start h-auto p-4" variant="outline">
                  <BookOpen className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">View All Projects</p>
                    <p className="text-sm text-gray-500">Manage assigned projects</p>
                  </div>
                </Button>
              </Link>
              <Link to="/pm/analytics">
                <Button className="w-full justify-start h-auto p-4" variant="outline" data-testid="pm-action-analytics">
                  <BarChart3 className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Project Analytics</p>
                    <p className="text-sm text-gray-500">Cross-project rollup &amp; charts</p>
                  </div>
                </Button>
              </Link>
              <Link to="/pm/team">
                <Button className="w-full justify-start h-auto p-4" variant="outline" data-testid="pm-action-team">
                  <Users className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Team Management</p>
                    <p className="text-sm text-gray-500">All members across projects</p>
                  </div>
                </Button>
              </Link>
              <Link to="/pm/meetings">
                <Button className="w-full justify-start h-auto p-4" variant="outline" data-testid="pm-action-meetings">
                  <Calendar className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Schedule Meetings</p>
                    <p className="text-sm text-gray-500">Plan, track &amp; run meetings</p>
                  </div>
                </Button>
              </Link>
              <Link to="/reimbursements">
                <Button className="w-full justify-start h-auto p-4" variant="outline" data-testid="pm-action-reimbursements">
                  <Receipt className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Reimbursements</p>
                    <p className="text-sm text-gray-500">Approve expense claims</p>
                  </div>
                </Button>
              </Link>
              <Link to="/financials">
                <Button className="w-full justify-start h-auto p-4" variant="outline" data-testid="pm-action-financials">
                  <CircleDollarSign className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Financials</p>
                    <p className="text-sm text-gray-500">Ledger &amp; per-funder breakdown</p>
                  </div>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Recent Projects */}
        {dashboardData?.recent_projects && dashboardData.recent_projects.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Managed Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData.recent_projects.map(project => (
                  <div key={project.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <Link to={`/projects/${project.id}`} className="text-lg font-medium text-blue-700 hover:text-blue-800">
                        {project.title}
                      </Link>
                      <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                      <div className="flex items-center mt-2 space-x-4">
                        <Badge variant={
                          project.status === 'executing' ? 'default' :
                          project.status === 'planning' ? 'secondary' :
                          project.status === 'closing' ? 'outline' : 'destructive'
                        }>
                          {project.status}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          Budget: {formatCurrency(project.budget)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Category: {project.category}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 text-right min-w-0 flex-shrink-0">
                      <p className="text-sm font-medium">{project.progress}% Complete</p>
                      <Progress value={project.progress} className="w-24 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProjectManagerDashboard;